/**
 * Link PDF / Image Tool
 *
 * Links (not embeds) a PDF page or raster image (PNG/JPG) as a live
 * underlay reference. The picked file is registered with the shared
 * LinkManager and a footprint element is created in the scene:
 * - images become a textured raster plane (same payload as the Image tool),
 * - PDFs become a page-sized reference plane until a renderer decodes them.
 * The registry entry keeps the blob URL so Manage Links can reload the exact
 * source without re-picking during the session.
 */

import { v4 as uuidv4 } from 'uuid';
import { LinkElement, LinkGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { LinkManager } from '../core/LinkManager';
import { ThreeEvent } from '@react-three/fiber';
import i18n from '../i18n';

/** Session-only blob handles per link id (browser File objects cannot be saved). */
const underlaySources = new Map<string, string>();

export function getLinkedUnderlayUrl(linkId: string): string | undefined {
    return underlaySources.get(linkId);
}

export class LinkPDFImageTool extends BaseTool {
    id = 'link_pdf_image';
    name = 'Link PDF / Image';
    cursor = 'alias';

    private linkManager: LinkManager;

    constructor(linkManager: LinkManager) {
        super();
        this.linkManager = linkManager;
    }

    onActivate(): void {
        // Dialog-driven command: pick the underlay file, then register the
        // live link immediately (BIM: Manage Format window skipped here).
        void this.pickFile('.pdf,image/png,image/jpeg,image/bmp,image/gif,image/webp').then((file) => {
            if (file) {
                void this.linkFile(file);
            }
            this.exitToSelect();
        });
    }

    private async linkFile(file: File): Promise<void> {
        if (!this.context) return;
        const isPdf = file.name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
        const url = URL.createObjectURL(file);

        const link = this.linkManager.linkFile(isPdf ? 'PDF' : 'Image', file.name, {
            positioning: 'OriginToOrigin',
            size: file.size,
        });
        underlaySources.set(link.id, url);

        let width = 8.5 * 0.0254;
        let height = 11 * 0.0254;
        if (!isPdf) {
            // Read the true pixel aspect: 1 m on the longest edge.
            const dims = await this.measureImage(url).catch(() => null);
            if (dims) {
                const longest = Math.max(dims.w, dims.h) || 1;
                width = (dims.w / longest) * 1;
                height = (dims.h / longest) * 1;
            }
        }

        const geometryData = {
            linkId: link.id,
            origin: [0, 0, 0],
            scale: [1, 1, 1],
            rotation: [0, 0, 0],
            boxSize: [width, height, 0.001],
        } as LinkGeometryData;

        const element: LinkElement = {
            id: uuidv4(),
            category: 'Other Links',
            type: `${isPdf ? 'Linked PDF' : 'Linked Image'}: ${link.name}`,
            parameters: {
                'File Path': file.name,
                'Status': link.status,
                'Underlay URL': url,
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(element);
        this.linkManager.setDocumentId(link.id, element.id);
        this.notify(i18n.t('tools.notify.pdfImageLinked', { kind: isPdf ? 'PDF' : i18n.t('tools.notify.kindImage'), fileName: file.name }), 'success');
    }

    private measureImage(url: string): Promise<{ w: number; h: number }> {
        return new Promise((resolve, reject) => {
            const img = new window.Image();
            img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
            img.onerror = reject;
            img.src = url;
        });
    }

    onPointerDown(_event: ThreeEvent<PointerEvent>): void {
        // Dialog-driven command: nothing to place interactively.
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void { }
    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }
}
